import { useAgentStore, type PageTour } from "@/stores/agent-store";
import { clearAnchorHint, showAnchorHint } from "@/lib/assistant-anchor-hint";

// Deterministic driver for /tour and /tour-all (no LLM, no network — like the @adr SPARK-0014-design-primitives-single-source@1 flows).
// For each route: navigate if needed, wait for the mounted page to register its tour
// (useAssistantTour), post the summary, then per stop: wait for the anchored element, scroll +
// pulse it (.assistant-highlight, same visuals as the HighlightElement tool), post the description,
// dwell (or advance early via Next), move on. Abort is checked between every step.

// /tour-all visits Home, then the pages in sidebar order (AppShell PILLARS).
// Every page listed here registers a tour via useAssistantTour.
export const TOUR_ALL_ROUTES = [
  "/",
  "/save/basics",
  "/save/documents",
  "/save/contact",
  "/save/pinboard",
  "/grow/map",
  "/grow/watch",
  "/grow/play",
  "/share",
  "/match",
];

const STEP_POLL_MS = 200;
const ELEMENT_TRIES = 15; // ~3s for an element to mount (navigation render)
const TOUR_TRIES = 25; // ~5s for a page to register its tour (mount + data load)
// Pacing: a generous base per presentation plus reading time scaled by the snippet's length
// (capped), so long stops linger and short ones don't drag. A step's own dwellMs overrides all.
const DWELL_MS = 9000;
const INTRO_DWELL_MS = 6000; // pillar descriptions are short; "highlighted shortly"
const DWELL_PER_CHAR_MS = 30;
const DWELL_EXTRA_CAP_MS = 12_000;
const CONTROLS_HINT_DWELL_MS = 2500; // base per control-button hint card (short texts)
const WELCOME_BREAK_MS = 10_000; // pause after the welcome message before the controls walk
const SECTIONS_INTRO_BREAK_MS = 5000; // pause after the sections announcement before the intro

export interface TourTexts {
  /** Appended to the very first summary message ("press Esc or Stop anytime"). */
  escHint: string;
  /** Posted when a page never registers a tour (uninstrumented/gated). */
  noTour: string;
  /** Posted when the tour ends normally. */
  finished: string;
  /**
   * The very first bubble of a fresh /tour-all run (skipped on resume): welcome + what the
   * controls do. A fixed break follows it, then the [TourDeps.controlsIntro] walk.
   */
  welcome?: string;
  /** Posted after the controls walk, announcing the sections intro. */
  sectionsIntro?: string;
  /** Posted before [TourDeps.ask]: pick a section, or the general tour continues. */
  question?: string;
  /** Posted before each FOLLOW-UP ask (a picked section finished); falls back to [question]. */
  questionNext?: string;
}

/** One pillar of the /tour-all intro: highlight the nav anchor and describe it. */
export interface TourIntroItem {
  anchor: string;
  description: string;
  color?: string;
  /** Hand-tuned display time for THIS item, verbatim (skips base + reading-time formula). */
  dwellMs?: number;
}

export interface TourDeps {
  navigate: (path: string) => void;
  currentPath: () => string;
  /**
   * Post a chat bubble; `color` ties the text to the matching sidebar marker (onboarding),
   * `indent` is the tour outline depth (0 section, 1 page summary, 2 stop), `meta` is the step's
   * persistence payload (section vocabulary: route pillar — save|grow|share|match|default — plus
   * the revisit target). Returning the message id makes the bubble revisitable.
   */
  post: (
    content: string,
    color?: string,
    indent?: number,
    meta?: { section: string; anchor?: string; route?: string },
  ) => string | void;
  texts: TourTexts;
  /** The pillar color for a route — colors the route's bubbles and element pulses. */
  colorFor?: (route: string) => string | undefined;
  /**
   * When set (/tour-all), the onboarding intro runs BEFORE the page walk: each item's nav anchor
   * pulses in its color, its description is posted in the same color, and the anchor keeps a
   * lasting `.assistant-tour-visited` marker until the tour ends.
   */
  intro?: TourIntroItem[];
  /**
   * Topbar intro items (search, launchers, account) — shown after [intro] on the GENERAL path
   * only; picking a section via [ask] skips them and jumps straight to that section's pages.
   */
  introTopbar?: TourIntroItem[];
  /**
   * The tour's own control buttons, walked right after [TourTexts.buttonsHint]: each anchor is
   * highlighted with a floating hint card (no chat bubble per button). Fresh runs only.
   */
  controlsIntro?: Array<{ anchor: string; hint: string }>;
  /**
   * Ask the user to pick a section after the pillar intro. Resolves with a pillar id ("save",
   * "grow", …) or null for "continue with the general tour" (explicit or timeout — the caller
   * owns the timeout). Only called on fresh /tour-all runs with [TourTexts.question] set.
   */
  ask?: (options: string[], followUp: boolean) => Promise<string | null>;
  /**
   * Builds the "try it yourself" follow-up posted after [TourTexts.finished], given the pillars
   * whose PAGES were actually toured (picked or walked; intro-only viewing does not count).
   * Return null for nothing.
   */
  finishExtra?: (touredPillars: string[]) => string | null;
  dwellMs?: number;
  introDwellMs?: number;
  /** Reading-time factor (ms per snippet character, capped); tests pass 0 for exact timing. */
  perCharMs?: number;
}

export interface TourController {
  /** End the tour now (Stop button, Esc, sends, manual navigation, unmount). Silent. */
  abort: () => void;
  /** Advance past the current dwell early (Next button). While paused: step ONE presentation
   *  forward and stay paused (never an implicit play). */
  next: () => void;
  /** Freeze the tour at the current stop (highlight stays); [resume] continues. */
  pause: () => void;
  resume: () => void;
  /** Jump to the next pillar's routes (during the intro: same as next). */
  skipSection: () => void;
  /** Revisit the previous shown step (auto-pauses; repeated calls walk further back). */
  back: () => void;
  /** Revisit the step a tour bubble belongs to (auto-pauses, navigates, re-highlights). */
  revisitMessage: (messageId: string) => void;
  /** Whether [messageId] is a revisitable tour step (drawer makes those bubbles clickable). */
  hasStep: (messageId: string) => boolean;
  /** Whether [path] is where the tour itself navigated (drawer aborts on any other change). */
  isExpectedPath: (path: string) => boolean;
  /** Resolves when the tour has fully ended (finished or aborted). */
  done: Promise<void>;
  /**
   * Resolves as soon as the tour's CONTENT is complete: at the natural finish (entering review
   * mode) or on an early abort — whichever comes first. Persist the transcript on this, not on
   * [done]: review mode keeps [done] pending until Stop/Esc, and a page refresh there would
   * otherwise lose the tour.
   */
  ended: Promise<void>;
}

/** One already-shown tour step — the revisit targets for Back and bubble clicks. */
interface ShownStep {
  route: string | null;
  anchor: string | null;
  color?: string;
  messageId?: string;
  // The snippet text, re-shown as the floating anchor card when the step is revisited.
  content?: string;
  // The dwell this step was shown for — so resume() can re-publish it and the countdown bar
  // survives pause/resume instead of vanishing until the next step.
  dwellMs?: number;
}

function findTour(route: string): PageTour | undefined {
  return Object.values(useAgentStore.getState().pageTours).find((t) => t.route === route);
}

/** First path segment — the pillar a route belongs to ("/grow/map" → "grow"; "/" → "home"). */
function pillarOf(route: string): string {
  if (route === "/") return "home";
  return route.replace(/^\//, "").split("/")[0] ?? "";
}

export function startTour(
  routes: string[],
  deps: TourDeps,
  opts?: {
    /**
     * Resume cursor: the tour continues AFTER this already-shown step.
     * - `route` set: applies to the FIRST route (the caller slices `routes` to start at the
     *   interrupted page): its summary is not re-posted, and stops up to and INCLUDING `anchor`
     *   are skipped (`anchor: null` = interrupted right after the summary, so all stops run).
     *   Later routes run whole. The caller passes no intro in this mode.
     * - `route` null: the interruption happened during the INTRO — intro items up to and
     *   INCLUDING `anchor` are skipped, the rest of the intro and all pages run whole.
     */
    skipUntil?: { route: string | null; anchor: string | null };
  },
): TourController {
  let aborted = false;
  let contentEnded!: () => void;
  const ended = new Promise<void>((resolve) => {
    contentEnded = resolve;
  });
  let paused = false;
  let inIntro = false;
  // Pillar to fast-forward past after Skip section (routes of this pillar are dropped).
  let skipFromPillar: string | null = null;
  // Pillar currently being presented (drives the current-section nav marker + Skip section).
  let currentPillar = "";
  // Steps already shown (intro items, page summaries, stops) — the revisit history.
  const shown: ShownStep[] = [];
  // -1 = at the live position; otherwise the index currently being revisited (Back walks it down).
  let revisitCursor = -1;
  let advance: (() => void) | null = null;

  // dwellMs: how long this step is about to be shown — feeds the drawer's countdown bar. 0 for
  // steps with no timed dwell (none call record without one today, but the default keeps the bar
  // hidden rather than animating a wrong duration).
  const record = (step: ShownStep, dwellMs = 0) => {
    shown.push({ ...step, dwellMs });
    // Mirror into the store: the drawer decides bubble clickability + the "you are here"
    // highlight during render, where reading this controller (a ref) is not allowed.
    if (step.messageId) useAgentStore.getState().addTourStepId(step.messageId);
    useAgentStore.getState().setTourCurrentStepId(step.messageId ?? null, dwellMs);
  };

  const setPaused = (p: boolean) => {
    paused = p;
    useAgentStore.getState().setTourPaused(p);
  };

  // Sleep that ends early on next()/abort(). Plain setTimeout — fake-timer friendly in tests.
  const rawWait = (ms: number) =>
    new Promise<void>((resolve) => {
      const t = setTimeout(() => {
        advance = null;
        resolve();
      }, ms);
      advance = () => {
        clearTimeout(t);
        advance = null;
        resolve();
      };
    });

  // While paused, Next/Skip release exactly ONE presentation step without switching back to play.
  let stepRelease = false;

  // Dwell waits honour Pause: after the timer (or an early advance) the gate holds the tour at
  // the CURRENT step — whatever is highlighted stays highlighted — until resume/abort, or ONE
  // step is released (Next while paused). Machinery polls (element/registration) use rawWait so
  // a released step runs through to its next presentation point instead of re-gating mid-flight.
  const wait = async (ms: number) => {
    await rawWait(ms);
    while (paused && !aborted && !stepRelease) await rawWait(STEP_POLL_MS);
    stepRelease = false;
  };

  // Every path the tour has legitimately been on: the origin + every route the driver itself
  // navigated to. The drawer aborts on any OTHER path (user walked away). A set, not a single
  // value: during a driver-initiated navigation React can re-render with the OLD path while
  // tourActive is already true — the origin must stay "expected" or the tour self-aborts before
  // the router commits the new location.
  const expectedPaths = new Set([deps.currentPath()]);

  // Highlight bookkeeping lives at controller scope: revisit() (Back / bubble click) manipulates
  // the same transient highlight the run loop owns.
  let highlighted: Element | null = null;
  // Nav elements carrying the lasting .assistant-tour-visited marker (intro trail). They keep
  // their --assistant-marker color until the tour ends.
  const visited: Element[] = [];
  // Page elements whose stop has already been presented — they keep .assistant-tour-seen so the
  // spotlight blur is forward-only (already-shown things stay sharp). Cleared at tour end.
  const seen: Element[] = [];
  const markSeen = (el: Element) => {
    el.classList.add("assistant-tour-seen");
    seen.push(el);
  };
  // Spotlight: while an element is highlighted, the page behind the overlay blurs (index.css
  // body.assistant-spotlight). Tied 1:1 to the transient highlight lifecycle.
  const setSpotlight = (on: boolean) =>
    document.body.classList.toggle("assistant-spotlight", on);
  // Ancestors that establish a stacking context (e.g. the z-30 topbar header) cap the highlighted
  // element's z-lift BELOW the blur overlay — the element would render blurred despite its own
  // z-index. Lift every such ancestor for the duration of the highlight.
  const lifted: Element[] = [];
  const liftAncestors = (el: Element) => {
    for (let a = el.parentElement; a && a !== document.body; a = a.parentElement) {
      const cs = getComputedStyle(a);
      const createsContext =
        (cs.position !== "static" && cs.zIndex !== "auto") ||
        cs.transform !== "none" ||
        cs.filter !== "none" ||
        parseFloat(cs.opacity) < 1 ||
        cs.isolation === "isolate" ||
        // CSS containment (e.g. Tailwind's @container on <main>) also establishes one.
        (cs.containerType !== undefined && cs.containerType !== "normal") ||
        /layout|paint|strict|content/.test(cs.contain ?? "");
      if (createsContext && !a.classList.contains("assistant-lift")) {
        a.classList.add("assistant-lift");
        lifted.push(a);
      }
    }
  };
  const clearLifts = () => {
    for (const el of lifted) el.classList.remove("assistant-lift");
    lifted.length = 0;
  };
  const highlight = (el: Element, color?: string, hint?: string) => {
    if (color) (el as HTMLElement).style.setProperty("--assistant-marker", color);
    el.classList.add("assistant-highlight");
    highlighted = el;
    liftAncestors(el);
    setSpotlight(true);
    // The chat snippet, readable right at the element (transient card, cleared with the pulse).
    if (hint) showAnchorHint(el, hint, color);
  };
  const clearHighlight = () => {
    clearAnchorHint();
    setSpotlight(false);
    clearLifts();
    if (highlighted) {
      highlighted.classList.remove("assistant-highlight");
      // Keep the color var if the element also carries the lasting marker.
      if (!visited.includes(highlighted)) {
        (highlighted as HTMLElement).style.removeProperty("--assistant-marker");
      }
      highlighted = null;
    }
  };

  // Revisit an already-shown step (Back / bubble click): auto-pause, go to its page, re-highlight
  // its element. Continue returns to the live position (the run loop self-heals the route).
  const revisit = async (index: number) => {
    const step = shown[index];
    if (!step || aborted) return;
    revisitCursor = index;
    setPaused(true);
    useAgentStore.getState().setTourCurrentStepId(step.messageId ?? null);
    clearHighlight();
    if (step.route && deps.currentPath() !== step.route) {
      expectedPaths.add(step.route);
      deps.navigate(step.route);
    }
    if (!step.anchor) return;
    // rawWait, not wait(): the pause gate would deadlock the revisit itself.
    let el: Element | null = null;
    for (
      let t = 0;
      t < ELEMENT_TRIES &&
      !(el = document.querySelector(`[data-assistant-anchor="${step.anchor}"]`)) &&
      !aborted;
      t++
    ) {
      await rawWait(STEP_POLL_MS);
    }
    if (!el || aborted || revisitCursor !== index) return; // superseded by a newer revisit
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    highlight(el, step.color, step.content);
  };

  const run = async () => {
    const baseDwell = deps.dwellMs ?? DWELL_MS;
    const perChar = deps.perCharMs ?? DWELL_PER_CHAR_MS;
    // Presentation time: explicit per-element override wins verbatim; otherwise base plus
    // capped reading time for the snippet's length.
    const dwellFor = (text: string, base: number, override?: number): number =>
      override ?? base + Math.min(DWELL_EXTRA_CAP_MS, text.length * perChar);
    // Outline depth: with a section intro, pages sit one level below the sections.
    const summaryIndent = deps.intro?.length ? 1 : 0;
    let first = true;
    const clearVisited = () => {
      for (const el of visited) {
        el.classList.remove("assistant-tour-visited");
        (el as HTMLElement).style.removeProperty("--assistant-marker");
      }
      visited.length = 0;
    };
    // The nav item of the pillar CURRENTLY being presented keeps a stronger colored marker
    // (.assistant-tour-current) while its pages are toured; it demotes to the visited trail when
    // the tour moves on.
    let currentNav: Element | null = null;
    const demoteCurrent = () => {
      if (!currentNav) return;
      currentNav.classList.remove("assistant-tour-current");
      if (!visited.includes(currentNav)) {
        (currentNav as HTMLElement).style.removeProperty("--assistant-marker");
      }
      currentNav = null;
    };
    const markCurrentSection = (route: string, color: string | undefined) => {
      const pillar = pillarOf(route);
      if (pillar === currentPillar) return;
      currentPillar = pillar;
      demoteCurrent();
      const nav = document.querySelector(`[data-assistant-anchor="nav.${pillar}"]`);
      if (nav) {
        // Without a color the CSS falls back to the theme accent.
        if (color) (nav as HTMLElement).style.setProperty("--assistant-marker", color);
        nav.classList.add("assistant-tour-current");
        currentNav = nav;
      }
    };
    const findElement = async (anchor: string): Promise<Element | null> => {
      let el: Element | null = null;
      for (
        let t = 0;
        t < ELEMENT_TRIES &&
        !(el = document.querySelector(`[data-assistant-anchor="${anchor}"]`)) &&
        !aborted;
        t++
      ) {
        await rawWait(STEP_POLL_MS); // machinery poll: not pause-gated (see wait())
      }
      return el;
    };
    const withEscHint = (content: string) => {
      if (!first) return content;
      first = false;
      return `${content}\n\n${deps.texts.escHint}`;
    };

    try {
      // Onboarding intro (/tour-all): walk the pillar nav items before touring any page.
      // Resuming an intro interruption: fast-forward past the items already shown (through the
      // cursor anchor); an unknown anchor (intro changed) skips the whole intro.
      let introSkip =
        opts?.skipUntil && opts.skipUntil.route === null ? opts.skipUntil.anchor : null;
      const runIntro = async (items: TourIntroItem[]): Promise<void> => {
        for (const item of items) {
          if (aborted) return;
          if (introSkip) {
            if (item.anchor === introSkip) introSkip = null;
            continue;
          }
          const el = await findElement(item.anchor);
          if (aborted) return;
          if (!el) continue; // sidebar anchor missing (shouldn't happen at the lg+ gate) → skip
          highlight(el, item.color, item.description);
          const introMsgId = deps.post(withEscHint(item.description), item.color, 0, {
            // Intro items: pillar anchors carry their pillar; topbar items are neutral.
            section: item.anchor.startsWith("nav.") ? item.anchor.slice(4) : "default",
            anchor: item.anchor,
          });
          const itemDwell = dwellFor(
            item.description,
            deps.introDwellMs ?? INTRO_DWELL_MS,
            item.dwellMs,
          );
          record(
            {
              route: null,
              anchor: item.anchor,
              color: item.color,
              messageId: typeof introMsgId === "string" ? introMsgId : undefined,
              content: item.description,
            },
            itemDwell,
          );
          await wait(itemDwell);
          // Swap the transient pulse for the lasting trail marker; the color var stays (the
          // element is registered as visited BEFORE clearing, so clearHighlight keeps it).
          el.classList.add("assistant-tour-visited");
          visited.push(el);
          clearHighlight();
          if (aborted) return;
        }
      };

      // Ask the drawer to pick a section; abort ends the race, `settled` stops the poll once
      // the answer won instead. Timeout/decline resolve null — the caller owns the timing.
      const askSection = async (options: string[], followUp: boolean): Promise<string | null> => {
        if (!deps.ask || !options.length) return null;
        let settled = false;
        const answer = await Promise.race([
          deps.ask(options, followUp).then((a) => {
            settled = true;
            return a;
          }),
          (async () => {
            while (!aborted && !settled) await rawWait(STEP_POLL_MS);
            return null;
          })(),
        ]);
        return aborted ? null : answer;
      };

      // NOTE: a tour WITHOUT an intro must reach the route walk synchronously (the drawer reads
      // isExpectedPath right after startTour; Next may fire before the first timer) — hence
      // every intro-phase await sits behind the intro?.length guard.
      let chosen: string | null = null;
      let pillarIds: string[] = [];
      if (deps.intro?.length) {
        // Fresh /tour-all runs open with the welcome bubble (tour purpose + how the controls
        // work), hold for a reading break (countdown visible), then point a floating hint card
        // at each control button, and finally announce the sections intro. Resumes skip all of
        // it. The controls walk replaces the esc hint that would otherwise ride the first post.
        if (deps.texts.welcome && !opts?.skipUntil) {
          const welcomeId = deps.post(deps.texts.welcome, undefined, 0, { section: "default" });
          record(
            {
              route: null,
              anchor: null,
              messageId: typeof welcomeId === "string" ? welcomeId : undefined,
              content: deps.texts.welcome,
            },
            WELCOME_BREAK_MS,
          );
          await wait(WELCOME_BREAK_MS);
          if (aborted) return;
          if (deps.controlsIntro?.length) {
            first = false;
            for (const control of deps.controlsIntro) {
              if (aborted) return;
              const el = await findElement(control.anchor);
              if (aborted) return;
              if (!el) continue; // e.g. Skip section absent on /tour
              highlight(el, undefined, control.hint);
              await wait(dwellFor(control.hint, CONTROLS_HINT_DWELL_MS));
              clearHighlight();
              if (aborted) return;
            }
          }
          if (deps.texts.sectionsIntro) {
            const sectionsId = deps.post(deps.texts.sectionsIntro, undefined, 0, {
              section: "default",
            });
            record(
              {
                route: null,
                anchor: null,
                messageId: typeof sectionsId === "string" ? sectionsId : undefined,
                content: deps.texts.sectionsIntro,
              },
              SECTIONS_INTRO_BREAK_MS,
            );
            await wait(SECTIONS_INTRO_BREAK_MS);
            if (aborted) return;
          }
        }
        inIntro = true;
        await runIntro(deps.intro);
        if (aborted) return;

        // After the sections: pick one, or continue with the general tour (topbar + all pages).
        // Resumes never ask.
        pillarIds = deps.intro
          .filter((i) => i.anchor.startsWith("nav."))
          .map((i) => i.anchor.slice(4));
        if (deps.ask && deps.texts.question && !opts?.skipUntil) {
          deps.post(deps.texts.question);
          chosen = await askSection(pillarIds, false);
          if (aborted) return;
        }
        if (!chosen && deps.introTopbar?.length) {
          await runIntro(deps.introTopbar);
          if (aborted) return;
        }
        inIntro = false;
      }

      // Resume: a routed cursor applies to the first page iteration only; later pages run whole.
      let resumeCursor = opts?.skipUntil?.route != null ? opts.skipUntil : null;
      // Pillars whose pages were actually shown — feeds the finished follow-up suggestions.
      const touredPillars = new Set<string>();
      const walkRoutes = async (rs: string[]): Promise<void> => {
      for (const route of rs) {
        if (aborted) return;
        const cursor = resumeCursor;
        resumeCursor = null;
        // Skip section: fast-forward past the remaining routes of the skipped pillar.
        if (skipFromPillar !== null) {
          if (pillarOf(route) === skipFromPillar) continue;
          skipFromPillar = null;
        }
        if (deps.currentPath() !== route) {
          expectedPaths.add(route);
          deps.navigate(route);
        }
        const color = deps.colorFor?.(route);
        markCurrentSection(route, color);

        // Wait for the mounted page to register its tour (covers navigation + data loading).
        let tour: PageTour | undefined;
        for (let i = 0; i < TOUR_TRIES && !(tour = findTour(route)); i++) {
          await rawWait(STEP_POLL_MS); // machinery poll: not pause-gated (see wait())
          if (aborted) return;
        }
        // Skip section pressed while this route was still setting up → drop it too.
        if (skipFromPillar !== null && pillarOf(route) === skipFromPillar) continue;
        if (!tour) {
          deps.post(deps.texts.noTour);
          continue;
        }
        touredPillars.add(pillarOf(route));

        // Resuming on this page: the summary (and the stops up to the cursor) were already
        // shown before the interruption — don't re-post them.
        let firstStopIdx = 0;
        if (cursor) {
          if (cursor.anchor) {
            // Cursor anchor gone (page content changed)? findIndex -1 → re-run the whole page.
            firstStopIdx = tour.stops.findIndex((s) => s.anchor === cursor.anchor) + 1;
          }
        } else {
          // Every bubble names its (sub)section: "**Save Documents**: This is your…".
          const summaryText = `**${tour.label}**: ${tour.summary}`;
          const summaryDwell = dwellFor(summaryText, baseDwell);
          const summaryMsgId = deps.post(withEscHint(summaryText), color, summaryIndent, {
            section: pillarOf(route) || "default",
            route,
          });
          record(
            {
              route,
              anchor: null,
              color,
              messageId: typeof summaryMsgId === "string" ? summaryMsgId : undefined,
            },
            summaryDwell,
          );
          await wait(summaryDwell);
          if (aborted) return;
        }

        for (const [i, stop] of tour.stops.entries()) {
          if (i < firstStopIdx) continue; // resume: already shown before the interruption
          if (skipFromPillar !== null) break; // Skip section pressed mid-page
          // Route self-heal: after a revisit to another page, walk back to the live route.
          if (deps.currentPath() !== route) {
            expectedPaths.add(route);
            deps.navigate(route);
          }
          // Wait for the anchored element; a stop whose element never appears (gated control,
          // empty list) is skipped silently.
          const el = await findElement(stop.anchor);
          if (aborted) return;
          if (!el) continue;

          el.scrollIntoView({ behavior: "smooth", block: "center" });
          // "**Save Documents (1/3)**: Upload files. Description…" — the chat snippet AND the
          // floating card at the element.
          const stopText = `**${tour.label} (${i + 1}/${tour.stops.length})**: ${stop.title}. ${stop.description}`;
          const stopDwell = dwellFor(stopText, baseDwell, stop.dwellMs);
          highlight(el, color, stopText);
          const stopMsgId = deps.post(stopText, color, summaryIndent + 1, {
            section: pillarOf(route) || "default",
            anchor: stop.anchor,
            route,
          });
          record(
            {
              route,
              anchor: stop.anchor,
              color,
              messageId: typeof stopMsgId === "string" ? stopMsgId : undefined,
              content: stopText,
            },
            stopDwell,
          );
          await wait(stopDwell);
          markSeen(el); // forward-only blur: this element stays sharp for the rest of the tour
          clearHighlight();
          if (aborted) return;
        }
      }
      };

      if (chosen) {
        // Section-by-section mode: walk ONLY the picked section, then offer the next pick.
        // Declining the follow-up (or its timeout) FINISHES the tour — only the picked
        // sections count as toured; running dry ends it too.
        let pool = routes;
        while (chosen) {
          const sectionRoutes = pool.filter((r) => pillarOf(r) === chosen);
          pool = pool.filter((r) => pillarOf(r) !== chosen);
          if (!sectionRoutes.length) break; // unknown pick (shouldn't happen) → finish
          await walkRoutes(sectionRoutes);
          if (aborted) return;
          const remaining = pillarIds.filter((p) => pool.some((r) => pillarOf(r) === p));
          if (!remaining.length) break;
          const nextQuestion = deps.texts.questionNext ?? deps.texts.question;
          if (nextQuestion) deps.post(nextQuestion);
          chosen = await askSection(remaining, true);
          if (aborted) return;
        }
      } else {
        await walkRoutes(routes);
        if (aborted) return;
      }
      deps.post(deps.texts.finished);
      // "Try it yourself" follow-up, scoped to the sections that were actually toured. A plain
      // outline step: its markdown links survive reload.
      const extra = deps.finishExtra?.([...touredPillars]);
      if (extra) deps.post(extra, undefined, 0, { section: "default" });
      // Review mode: the finished tour stays interactive — controls remain, Back and bubble
      // clicks revisit any step, markers stay visible. Only Stop/Esc (abort) exits and tears
      // down. Modeled as a permanent pause; the loop just waits for the abort.
      setPaused(true);
      useAgentStore.getState().setTourFinished(true);
      // Content complete — persist NOW, review mode can outlive the page. Resolved AFTER the
      // flags so ended-handlers can read tourFinished=true to tell finish from interruption.
      contentEnded();
      while (!aborted) await rawWait(STEP_POLL_MS);
    } finally {
      clearHighlight();
      demoteCurrent();
      clearVisited();
      for (const el of seen) el.classList.remove("assistant-tour-seen");
      seen.length = 0;
      setPaused(false);
      useAgentStore.getState().setTourFinished(false);
      useAgentStore.getState().setTourCurrentStepId(null);
      useAgentStore.getState().setTourActive(false);
      // Abort / error path (no-op after a natural finish already resolved it). Last, after the
      // teardown above, so ended-handlers observe the settled flags.
      contentEnded();
    }
  };

  useAgentStore.getState().clearTourStepIds();
  useAgentStore.getState().setTourActive(true);
  const done = run();
  void done.catch(() => undefined); // ended() in finally already covers the error path

  return {
    abort: () => {
      aborted = true;
      setPaused(false);
      advance?.();
    },
    next: () => {
      // While revisiting, Next is the symmetric counterpart of Back: walk the shown history
      // FORWARD one step (still paused). Only once the history is exhausted (back at the live
      // step) does Next step the live tour itself.
      if (paused && revisitCursor !== -1 && revisitCursor < shown.length - 1) {
        void revisit(revisitCursor + 1);
        return;
      }
      revisitCursor = -1;
      // While paused, Next steps ONE presentation forward and stays paused there — it must not
      // implicitly press play. Running tours just advance past the current dwell.
      if (paused) stepRelease = true;
      advance?.();
    },
    pause: () => {
      if (!aborted) setPaused(true);
    },
    resume: () => {
      revisitCursor = -1;
      // Back at the live position: the last shown step is what's on screen again. Re-publish its
      // dwell (not 0) so the countdown bar stays visible through pause/resume rather than
      // vanishing until the next step arrives.
      const live = shown.at(-1);
      useAgentStore.getState().setTourCurrentStepId(live?.messageId ?? null, live?.dwellMs ?? 0);
      setPaused(false);
    },
    back: () => {
      // From the live position the step ON SCREEN is the last shown one → go to the one before it;
      // while revisiting, walk further back.
      const index = revisitCursor === -1 ? shown.length - 2 : revisitCursor - 1;
      if (index >= 0) void revisit(index);
    },
    revisitMessage: (messageId) => {
      const index = shown.findIndex((s) => s.messageId === messageId);
      if (index >= 0) void revisit(index);
    },
    hasStep: (messageId) => shown.some((s) => s.messageId === messageId),
    skipSection: () => {
      // Like Next, skipping must not implicitly press play: while paused it releases one step
      // (landing at the next section's first presentation, still paused).
      if (paused) stepRelease = true;
      if (inIntro) {
        // During the intro a "section" is one pillar item — skipping it is just advancing.
        advance?.();
        return;
      }
      skipFromPillar = currentPillar || pillarOf(deps.currentPath());
      advance?.();
    },
    // ponytail: paths stay in the set for the whole run, so revisiting an earlier tour page
    // manually mid-tour won't abort — acceptable; any truly foreign path still does.
    isExpectedPath: (path) => expectedPaths.has(path),
    done,
    ended,
  };
}
